const DEFAULT_ERROR = "We couldn't complete that action. Please try again.";

const MESSAGE_REPLACEMENTS = new Map([
  ["one or more validation errors occurred", "Please check the entered information and try again."],
  ["validation failed", "Please check the entered information and try again."],
  ["invalid payload", "Please check the entered information and try again."],
  ["bad request", "Please check the entered information and try again."],
  ["unauthorized", "Your session has expired. Please sign in again."],
  ["forbidden", "You don't have permission to perform this action."],
  ["access denied", "You don't have permission to perform this action."],
  ["not found", "The requested information could not be found."],
  ["too many requests", "Too many attempts. Please wait a moment and try again."],
  ["failed to fetch", "Unable to connect to the server. Check your internet connection and try again."],
  ["network request failed", "Unable to connect to the server. Check your internet connection and try again."],
  ["networkerror when attempting to fetch resource", "Unable to connect to the server. Check your internet connection and try again."],
  ["login failed", "Sign-in failed. Check your phone number and password."],
  ["no token returned from api", "Sign-in could not be completed. Please try again."],
  ["invalid token payload", "Sign-in could not be completed. Please try again."],
  ["failed to logout user", "Could not sign out. Please try again."],
  ["select resturant", "Please select a restaurant."],
  ["no item selected please select items", "Please select at least one item."],
  ["food item(s) not found", "No matching food items were found."],
  ["password change successfully", "Password changed successfully."],
  ["tax updated successfully", "Tax settings updated successfully."],
  ["waiter request status updated successfully", "Waiter request updated successfully."],
  ["failed to upload gallery image", "The image could not be uploaded. Please try again."],
  ["category already exists", "This category already exists."],
  ["please select an image first", "Select an image before continuing."],
  ["discount cannot be greater than subtotal", "Discount cannot be greater than the subtotal."],
  ["refund attempt recorded as failed", "The refund could not be completed. The failed attempt was recorded."],
  ["selected bill quantity voided", "The selected item quantity was voided."],
  ["refund kot adjustment acknowledged", "The refund adjustment was marked as reviewed."],
]);

const cleanKey = value => String(value || "")
  .trim()
  .replace(/[.!]+$/, "")
  .replace(/\s+/g, " ")
  .toLowerCase();

const flattenValidationErrors = errors => {
  if (!errors || typeof errors !== "object") return [];
  return Object.values(errors)
    .flatMap(value => Array.isArray(value) ? value : [value])
    .filter(value => typeof value === "string" && value.trim())
    .map(value => value.trim());
};

const extractMessage = (value, seen = new Set()) => {
  if (value == null) return "";
  if (value instanceof Error) return extractMessage(value.message, seen);

  if (typeof value === "string") {
    let text = value.trim();
    if (!text) return "";

    if ((text.startsWith("{") && text.endsWith("}")) ||
        (text.startsWith("[") && text.endsWith("]")) ||
        (text.startsWith('"') && text.endsWith('"'))) {
      try { return extractMessage(JSON.parse(text), seen); } catch (_) { /* keep readable text */ }
    }

    text = text.replace(/^Error:\s*/i, "").trim();
    if (/^login failed\s*:/i.test(text)) {
      const detail = text.replace(/^login failed\s*:/i, "").trim();
      return extractMessage(detail, seen) || "Login failed";
    }
    return text;
  }

  if (typeof value !== "object" || seen.has(value)) return "";
  seen.add(value);

  const validationErrors = flattenValidationErrors(value.errors);
  if (validationErrors.length) return [...new Set(validationErrors)].slice(0, 2).join(" ");

  for (const key of ["message", "response", "detail", "error_description", "error", "title"]) {
    const candidate = extractMessage(value[key], seen);
    if (candidate) return candidate;
  }
  return "";
};

const looksTechnical = text =>
  /(^|\s)(system\.|microsoft\.|typeerror:|referenceerror:|sql(exception)?|stack trace|innerexception| at [\w.]+\(|unexpected token|unexpected end of json|status code 5\d\d)/i.test(text) ||
  /https?:\/\/tools\.ietf\.org\/html\/rfc/i.test(text);

export const getUserFriendlyMessage = (value, fallback = DEFAULT_ERROR) => {
  const extracted = extractMessage(value);
  if (!extracted) return fallback;

  const replacement = MESSAGE_REPLACEMENTS.get(cleanKey(extracted));
  if (replacement) return replacement;

  if (/timeout|timed out/i.test(extracted))
    return "The request took too long. Please try again.";
  if (/\b(401|token expired|session expired)\b/i.test(extracted))
    return "Your session has expired. Please sign in again.";
  if (/\b(403|not authorized|permission denied)\b/i.test(extracted))
    return "You don't have permission to perform this action.";
  if (/\b429\b|rate limit/i.test(extracted))
    return "Too many attempts. Please wait a moment and try again.";
  if (/\b(500|502|503|504)\b|internal server error|service unavailable/i.test(extracted) || looksTechnical(extracted))
    return fallback;
  if (/^\s*<(!doctype|html)/i.test(extracted) || extracted === "[object Object]")
    return fallback;

  return extracted;
};

export const normalizeToastContent = (content, type = "info") => {
  if (typeof content !== "string" && !(content instanceof Error) &&
      (!content || typeof content !== "object" || content.$$typeof)) return content;

  const fallback = type === "success"
    ? "Action completed successfully."
    : type === "warning"
      ? "Please review the information and try again."
      : type === "info"
        ? "Here is an update."
        : DEFAULT_ERROR;
  return getUserFriendlyMessage(content, fallback);
};

export { DEFAULT_ERROR };
